'use client';

/**
 * Card — the cream paper card shell used everywhere in the app.
 *
 * Two things this primitive centralizes:
 *   1. The `1px solid var(--ink)` border
 *   2. The cream paper fill (--paper-2 default, --paper-1 for nested
 *      cards or where a slightly darker fill reads better)
 *
 * Everything else — padding, layout (flex / grid / block), gap,
 * width / height, margin — lives on the call site via `style`.
 * The component is intentionally minimal: 99% of the app's cards
 * are just <div> wrappers with a border + bg, so a fat API would
 * encourage premature consolidation. Pass what you need.
 *
 * Use cases:
 *   • Detail-modal containers (yarn / needle / project-needle).
 *   • Form sections — YARN, NEEDLES, TECHNIQUES groups.
 *   • Tile-row cards (yarn, needle, technique list rows). Note:
 *     those have a richer two-column tile+body shape — the
 *     <TileRowCard> primitive (phase 4.E) will compose Card with
 *     that specific layout. For now they pass `style` directly.
 *
 * Not polymorphic. If you need a button, wrap a <Card> in <Link>
 * or <button style={{ all: 'unset' }}>. Keeps the recipe simple.
 */

import type { CSSProperties, ReactNode } from 'react';

export type CardTone = 'paper-1' | 'paper-2';

export type CardProps = {
  children: ReactNode;
  /** Fill color. Default 'paper-2' (lifted cream). */
  tone?: CardTone;
  /**
   * Set true for the standard 12px-all-sides padding (Veronika
   * 2026-05-12, picked from the build-1361 padding sandbox).
   * Default false leaves padding to the caller — used by
   * layout-driven cards (yarn tile-rows, segmented controls, etc.).
   */
  padded?: boolean;
  /** Per-instance overrides — different padding, layout, etc. */
  style?: CSSProperties;
  className?: string;
};

const TONE_BG: Record<CardTone, string> = {
  'paper-1': 'var(--paper-1)',
  'paper-2': 'var(--paper-2)',
};

/** The canonical card padding — 12px all sides. */
export const CARD_PADDING = 12;

export function Card({ children, tone = 'paper-2', padded = false, style, className }: CardProps) {
  return (
    <div
      className={className}
      style={{
        border: '1px solid var(--ink)',
        background: TONE_BG[tone],
        ...(padded ? { padding: CARD_PADDING } : null),
        ...style,
      }}
    >
      {children}
    </div>
  );
}
